/**
 * Staff Portal v2 — navigation configuration (approved TrinitiCare design).
 *
 * Top-level order is the approved nine-item sidebar:
 * Dashboard, My Shifts, My Participants, Safety & Compliance, Learning,
 * Payroll, My Employment, Resources, Settings.
 *
 * Every entry declares its permissions and destination route. The shell
 * filters by `hasPermission()` client-side (presentation only — every
 * destination page and API separately enforces RBAC server-side).
 */
import {
  LayoutDashboard, Briefcase, Users, ShieldCheck, GraduationCap,
  IdCard, BookOpen, Settings as SettingsIcon, Wallet,
  Calendar, Clock, FileText, Plane, CalendarClock, ArrowLeftRight,
  Route as RouteIcon, DollarSign, MapPin, Bell, Menu, Home,
} from 'lucide-react';

export const PRIMARY_NAV = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    to: '/staff/dashboard',
    icon: LayoutDashboard,
    permissions: [],
    testId: 'nav-dashboard',
  },
  {
    key: 'my-shifts',
    label: 'My Shifts',
    to: '/my/work',
    icon: Briefcase,
    permissions: [],
    testId: 'nav-my-shifts',
    submenu: [
      { key: 'ms-overview', label: 'Overview', to: '/my/work', icon: Briefcase, permissions: [], testId: 'sub-nav-my-work-overview' },
      { key: 'ms-roster', label: 'Roster', to: '/staff/roster', icon: Calendar, permissions: ['roster.view'], testId: 'sub-nav-my-roster' },
      { key: 'ms-open-shifts', label: 'Open Shifts', to: '/staff/open-shifts', icon: MapPin, permissions: ['roster.open_shift_pickup'], testId: 'sub-nav-my-open-shifts' },
      { key: 'ms-swaps', label: 'Shift Swaps', to: '/my/swap-requests', icon: ArrowLeftRight, permissions: ['roster.request_swap'], testId: 'sub-nav-my-swaps' },
      { key: 'ms-clock', label: 'Clock In/Out', to: '/my/clock', icon: Clock, permissions: ['attendance.clock_in', 'attendance.clock_out'], testId: 'sub-nav-my-clock' },
      { key: 'ms-timesheets', label: 'Timesheets', to: '/timesheets/my-v2', icon: FileText, permissions: ['timesheets.view_own'], testId: 'sub-nav-my-timesheets' },
      { key: 'ms-leave', label: 'Leave', to: '/portal/leave', icon: Plane, permissions: ['leave.request'], testId: 'sub-nav-my-leave' },
      { key: 'ms-availability', label: 'Availability', to: '/portal/availability', icon: CalendarClock, permissions: ['availability.manage'], testId: 'sub-nav-my-availability' },
      { key: 'ms-trips', label: 'Trip Logs', to: '/my/trip-logs', icon: RouteIcon, permissions: ['transport.log', 'transport.view_own'], testId: 'sub-nav-my-trips' },
      { key: 'ms-expenses', label: 'Expenses', to: '/my/expenses', icon: DollarSign, permissions: ['expenses.submit', 'expenses.view_own'], testId: 'sub-nav-my-expenses' },
    ],
  },
  {
    key: 'my-participants',
    label: 'My Participants',
    to: '/staff/participants',
    icon: Users,
    permissions: ['participants.view'],
    testId: 'nav-my-participants',
  },
  {
    key: 'safety',
    label: 'Safety & Compliance',
    to: '/compliance/incidents',
    icon: ShieldCheck,
    permissions: ['incidents.view'],
    testId: 'nav-safety',
  },
  {
    key: 'learning',
    label: 'Learning',
    to: '/staff/learning',
    icon: GraduationCap,
    permissions: [],
    testId: 'nav-learning',
  },
  {
    key: 'payroll',
    label: 'Payroll',
    to: '/staff/payroll',
    icon: Wallet,
    permissions: [],
    testId: 'nav-payroll',
  },
  {
    key: 'employment',
    label: 'My Employment',
    to: '/staff/employment',
    icon: IdCard,
    permissions: [],
    testId: 'nav-employment',
  },
  {
    key: 'properties',
    label: 'Properties / Houses',
    to: '/accommodation/houses',
    icon: Home,
    permissions: ['houses.view'],
    testId: 'nav-properties',
  },
  {
    key: 'resources',
    label: 'Resources',
    to: '/staff/resources',
    icon: BookOpen,
    permissions: [],
    testId: 'nav-resources',
  },
  {
    key: 'settings',
    label: 'Settings',
    to: '/settings',
    icon: SettingsIcon,
    permissions: [],
    testId: 'nav-settings',
  },
];

// Mobile bottom nav — Support Workers reach a Participant Profile only via a shift,
// so there is no standalone Participants entry here.
export const MOBILE_BOTTOM_NAV = [
  { key: 'm-home', label: 'Home', to: '/staff/dashboard', icon: LayoutDashboard, testId: 'mnav-home' },
  { key: 'm-shifts', label: 'Shifts', to: '/staff/roster', icon: Calendar, testId: 'mnav-shifts' },
  { key: 'm-alerts', label: 'Alerts', to: '/notifications', icon: Bell, testId: 'mnav-alerts' },
  { key: 'm-more', label: 'More', to: null, icon: Menu, testId: 'mnav-more' },
];

// Filter helper — used by all shell surfaces. Empty permissions[] = always show
// (route protection happens at the destination anyway).
export function filterNavByPermission(items, hasPermission) {
  return items.filter((item) => {
    if (!item.permissions || item.permissions.length === 0) return true;
    return item.permissions.some((p) => hasPermission(p));
  });
}
